import { ITaskResource } from 'src/generated-types/scheduledTask'

const SUCCESSION_WALK_LIMIT = 100

export const HANDOVER_GRACE_DAYS = 1

export const startOfDay = (value: Date | string) => {
  const date = new Date(value)
  date.setHours(0, 0, 0, 0)
  return date
}

export const addDays = (value: Date | string, days: number) => {
  const date = startOfDay(value)
  date.setDate(date.getDate() + days)
  return date
}

export const toApiDate = (value: Date | string) => {
  const day = startOfDay(value)
  return new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()))
}

export const latestOf = (...dates: (Date | undefined)[]) => {
  const known = dates.filter((date): date is Date => date != null)
  return known.length ? new Date(Math.max(...known.map(date => date.getTime()))) : undefined
}

export const earliestOf = (...dates: (Date | undefined)[]) => {
  const known = dates.filter((date): date is Date => date != null)
  return known.length ? new Date(Math.min(...known.map(date => date.getTime()))) : undefined
}

export const today = () => startOfDay(new Date())

export const editableFrom = () => addDays(new Date(), -HANDOVER_GRACE_DAYS)

export const isWritableDay = (value?: Date | string | null) =>
  value != null && startOfDay(value).getTime() >= editableFrom().getTime()

export const isOngoing = (endDate?: Date | string | null) =>
  endDate != null && startOfDay(endDate).getTime() >= today().getTime()

export interface ITaskResourceSuccession {
  root: ITaskResource
  members: ITaskResource[]
}

export interface IVesselDisplayRow {
  id: string | number
  isHead: boolean
  isReplaced: boolean
  rootId: number
  name: string
  chain: string
  description: string
  startDate?: Date
  endDate?: Date
  expanded: boolean
  hasHistory: boolean
  taskResource: ITaskResource
  predecessor?: ITaskResource
  successor?: ITaskResource
  canReplace: boolean
  canEdit: boolean
  canRevert: boolean
  canDelete: boolean
}

const toTime = (value?: Date | string | null) => (value ? new Date(value).getTime() : 0)

const vesselName = (taskResource?: ITaskResource) => taskResource?.resource?.name ?? taskResource?.resourceName ?? ''

export const clampStartDate = (taskResource: ITaskResource, workOrderStart?: Date) => {
  if (!taskResource.startDate) return undefined
  if (!workOrderStart) return taskResource.startDate
  return toTime(taskResource.startDate) < toTime(workOrderStart) ? workOrderStart : taskResource.startDate
}

export const clampEndDate = (taskResource: ITaskResource, workOrderEnd?: Date) => {
  if (!taskResource.endDate) return undefined
  if (!workOrderEnd) return taskResource.endDate
  return toTime(taskResource.endDate) > toTime(workOrderEnd) ? workOrderEnd : taskResource.endDate
}

export const buildSuccessions = (taskResources: ITaskResource[]): ITaskResourceSuccession[] => {
  const byId = new Map<number, ITaskResource>(taskResources.map(row => [row.id, row]))
  const successorOf = new Map<number, ITaskResource>()

  taskResources.forEach(row => {
    if (row.replacedTaskResourceId != null) successorOf.set(row.replacedTaskResourceId, row)
  })

  const roots = taskResources
    .filter(row => row.replacedTaskResourceId == null || !byId.has(row.replacedTaskResourceId))
    .sort((a, b) => toTime(a.startDate) - toTime(b.startDate) || a.id - b.id)

  return roots.map(root => {
    const members: ITaskResource[] = [root]
    let current = root
    let steps = 0

    while (steps++ < SUCCESSION_WALK_LIMIT) {
      const next = successorOf.get(current.id)
      if (!next) break
      members.push(next)
      current = next
    }

    return { root, members }
  })
}

export const buildVesselDisplayRows = ({
  successions,
  expandedRoots,
  canAct,
  workOrderStart,
  workOrderEnd,
}: {
  successions: ITaskResourceSuccession[]
  expandedRoots: Record<number, boolean>
  canAct: boolean
  workOrderStart?: Date
  workOrderEnd?: Date
}): IVesselDisplayRow[] => {
  const display: IVesselDisplayRow[] = []

  successions.forEach(({ root, members }) => {
    const hasHistory = members.length > 1
    const expanded = hasHistory && !!expandedRoots[root.id]
    const headIndex = members.length - 1

    const workOrderHasRoom = (member: ITaskResource) =>
      !workOrderEnd || !member.startDate || startOfDay(member.startDate) < startOfDay(workOrderEnd)

    const toRow = (member: ITaskResource, index: number): IVesselDisplayRow => {
      const isHead = index === headIndex

      return {
        id: member.id,
        isHead,
        isReplaced: !isHead,
        rootId: root.id,
        name: vesselName(member),
        chain: isHead && hasHistory ? members.map(vesselName).join(' → ') : '',
        description: member.description ?? '',
        startDate: clampStartDate(member, workOrderStart),
        endDate: clampEndDate(member, workOrderEnd),
        expanded: isHead ? expanded : false,
        hasHistory,
        taskResource: member,
        predecessor: members[index - 1],
        successor: members[index + 1],
        canReplace: canAct && isHead && isOngoing(member.endDate) && workOrderHasRoom(member),
        canEdit: canAct && (isHead || isWritableDay(member.startDate) || isWritableDay(member.endDate)),
        canRevert: canAct && isHead && hasHistory && isWritableDay(member.startDate),
        canDelete: canAct && isHead && !hasHistory,
      }
    }

    display.push(toRow(members[headIndex], headIndex))

    if (!expanded) return

    for (let index = headIndex - 1; index >= 0; index--) display.push(toRow(members[index], index))
  })

  return display
}
